import React from "react";
import {Button, CheckBox, Column, Row, RowLR, Text, Spinner, TextInput, Select, TimeSpanInput} from "react-vcomponents";
import {BaseComponent} from "react-vextensions";
import {ES, InfoButton, TextPlus, minuteInMS} from "web-vcore";
import {ControlInput} from "../../../@Shared/ControlInput.js";
import {EngineConfigPanels_SharedProps, ExtendEngineConfigPanelSharedProps} from "../ConfigPanel_Local.js";
import {SpinnerForPercent} from "../../../../Utils/ReactComponents/SpinnerForPercent.js";
import {EffectPointerInput} from "../../../@Shared/EffectPointerInput.js";
import {EffectPointer} from "../../../../Server/DB/@Shared/EffectPointer.js";
import {GetEntries} from "js-vextensions";
import {DesyncCalcType, SirenType, PanDirection, LongInhaleDetectionConfig} from "../../../../Server/DB/engineConfigs/@EngineConfig/@EC_Breath.js";
import {BREATH_INTERVAL_CALC_WINDOW_SIZE_LONG, BREATH_INTERVAL_PERCENTILE_X_CALC_WINDOW_SIZE, BREATH_INTERVAL_PERCENTILE_X_THRESHOLD_X, BREATH_INTERVAL_PERCENTILE_X_THRESHOLD_Y, BREATH_PEAKS_CALC_WINDOW_SIZE_DEFAULT} from "../../../Timeline/Sessions/SessionUI/SessionChartColors.js";
import {FieldLabel, flp} from "../../../@Shared/FieldLabel.js";
import {styles} from "../../../../Utils/UI/GlobalStyles.js";
import {ShowMessageBox} from "react-vmessagebox";

const GetSharedProps = (props: EngineConfigPanels_SharedProps)=>ExtendEngineConfigPanelSharedProps(props, c=>c.breath, (c, v)=>c.breath = v);

export class Breath_ConfigUI extends BaseComponent<{client?: boolean} & EngineConfigPanels_SharedProps, {}> {
	render() {
		const {client, enabled} = this.props;
		const sharedProps = GetSharedProps(this.props);
		const {config, ChangeConfig} = sharedProps;
		const fp = (fieldName: keyof typeof config)=>`breath.${fieldName}`;

		const splitAt = 260;
		return (
			<Column mt={15} style={{background: "hsla(0,0%,100%,.1)", borderRadius: 5, padding: 5}}>
				<Row style={{fontSize: 18}}>
					<CheckBox text="Breath" enabled={enabled} value={config.enabled} onChange={val=>ChangeConfig(c=>c.enabled = val)}/>
				</Row>

				{config.enabled &&
				<>
					<RowLR mt={5} splitAt={splitAt}>
						<Text p={flp}>Record:</Text>
						<CheckBox text="Raw data" enabled={enabled} value={config.recordData_raw} onChange={val=>ChangeConfig(c=>c.recordData_raw = val)}/>
						<InfoButton ml={5} text="The history of raw force samples. Storage cost: ~0.3mb/hour (only stored in browser's IndexedDB, until exported)"/>
					</RowLR>
					<RowLR mt={5} splitAt={splitAt}>
						<FieldLabel text="Max sample-gap in sequence (base):" fieldPath={fp("maxSampleGapInSequence_base")} info={`
							The maximum allowed time-gap (in seconds) between consecutive samples, for them to be considered part of the same continuous sequence.
							(affects things like DEILD-attempt-start detections based on breath-data patterns; without this setting, a large gap in samples can easily cause false triggers)
							(0 = disable this functionality)
						`.AsMultiline(0)}/>
						<Spinner enabled={enabled} min={0} step={.1} value={config.maxSampleGapInSequence_base} onChange={val=>ChangeConfig(c=>c.maxSampleGapInSequence_base = val)}/>
						<Text ml={5}>s</Text>
					</RowLR>

					<Row mt={15} style={{fontSize: 18}}>Sync</Row>
					<RowLR mt={5} splitAt={splitAt}>
						<FieldLabel text="Sync enabled:" fieldPath={fp("sync_enabled")}/>
						<CheckBox enabled={enabled} value={config.sync_enabled} onChange={val=>ChangeConfig(c=>c.sync_enabled = val)}/>
					</RowLR>

					{config.sync_enabled &&
					<>
						<RowLR mt={5} splitAt={splitAt}>
							<FieldLabel text="Increase cycle time:" fieldPath={fp("sync_increaseCycleTime_triggerSet")}/>
							<ControlInput enabled={enabled} value={config.sync_increaseCycleTime_triggerSet} onChange={val=>ChangeConfig(c=>c.sync_increaseCycleTime_triggerSet = val)}/>
						</RowLR>
						<RowLR mt={5} splitAt={splitAt}>
							<FieldLabel text="Decrease cycle time:" fieldPath={fp("sync_decreaseCycleTime_triggerSet")}/>
							<ControlInput enabled={enabled} value={config.sync_decreaseCycleTime_triggerSet} onChange={val=>ChangeConfig(c=>c.sync_decreaseCycleTime_triggerSet = val)}/>
						</RowLR>
						<RowLR mt={5} splitAt={splitAt}>
							<FieldLabel text="Increase min volume:" fieldPath={fp("sync_increaseMinVolume_triggerSet")}/>
							<ControlInput enabled={enabled} value={config.sync_increaseMinVolume_triggerSet} onChange={val=>ChangeConfig(c=>c.sync_increaseMinVolume_triggerSet = val)}/>
						</RowLR>
						<RowLR mt={5} splitAt={splitAt}>
							<FieldLabel text="Decrease min volume:" fieldPath={fp("sync_decreaseMinVolume_triggerSet")}/>
							<ControlInput enabled={enabled} value={config.sync_decreaseMinVolume_triggerSet} onChange={val=>ChangeConfig(c=>c.sync_decreaseMinVolume_triggerSet = val)}/>
						</RowLR>
						<RowLR mt={5} splitAt={splitAt}>
							<FieldLabel text="Block breath-interval effects:" fieldPath={fp("sync_blockBreathIntervalEffects_triggerSet")} info={`
								Example use-case: Combine with a hotkey that force-switches the phase from dreaming to drifting.
								(if dreaming had started due to breath-interval data, it'd otherwise just immediately switch back to dreaming afterward)
							`.AsMultiline(0)}/>
							<ControlInput enabled={enabled} value={config.sync_blockBreathIntervalEffects_triggerSet} onChange={val=>ChangeConfig(c=>c.sync_blockBreathIntervalEffects_triggerSet = val)}/>
						</RowLR>
						<RowLR mt={5} splitAt={splitAt}>
							<FieldLabel text="Block duration" fieldPath={fp("sync_blockBreathIntervalEffects_duration")}/>
							<Spinner enabled={enabled} min={0} step="any" value={config.sync_blockBreathIntervalEffects_duration} onChange={val=>ChangeConfig(c=>c.sync_blockBreathIntervalEffects_duration = val)}/>
							<Text ml={5}>minutes</Text>
						</RowLR>

						<RowLR mt={5} splitAt={splitAt}>
							{/*<Text>Voice, tag:</Text>
							<TextInput ml={5} enabled={enabled} style={{width: 80}} value={config.voice_soundTag} onChange={val=>ChangeConfig(c=>c.voice_soundTag = val)}/>*/}
							<FieldLabel text="Voice, volume multiplier:" fieldPath={fp("voice_volumeMultiplier")}/>
							<SpinnerForPercent enabled={enabled} min={0} max={999} value={config.voice_volumeMultiplier} onChange={val=>ChangeConfig(c=>c.voice_volumeMultiplier = val)}/>
						</RowLR>
						<RowLR mt={5} splitAt={splitAt}>
							<FieldLabel text="Snooze fade-off duration:" fieldPath={fp("sync_snoozeFadeOffDuration")} info={`
								When a snooze is close to ending, how long to have a transition of effect volume/brightness from defined minimum to regular/calculated value.
							`.AsMultiline(0)}/>
							<Spinner enabled={enabled} min={0} step={1} value={config.sync_snoozeFadeOffDuration} onChange={val=>ChangeConfig(c=>c.sync_snoozeFadeOffDuration = val)}/>
							<Text ml={5}>s</Text>
						</RowLR>

						<Row mt={15} style={{fontSize: 18, fontWeight: "bold"}}>Spatial</Row>

						<RowLR mt={5} splitAt={splitAt}>
							<FieldLabel text="Spatial effects enabled:" fieldPath={fp("spatial_enabled")} info={`
								Whether to apply panning left<>right based on breathing data. (ie. this becomes the global driver for "simple" panning)
							`.AsMultiline(0)}/>
							<CheckBox enabled={enabled} value={config.spatial_enabled} onChange={val=>ChangeConfig(c=>c.spatial_enabled = val)}/>
						</RowLR>
						{config.spatial_enabled && <>
							<RowLR mt={5} splitAt={splitAt}>
								<FieldLabel text="Min breath value reversal:" fieldPath={fp("spatial_minBreathValueReversal")} info={`
									After an inhale/exhale reversal, the next reversal is blocked until the breath value has changed by this amount from the reversal point.
									Purpose: To prevent rapid flip-flopping near the inhale/exhale threshold.
								`.AsMultiline(0)}/>
								<Spinner enabled={enabled} min={0} step="any" value={config.spatial_minBreathValueReversal} onChange={val=>ChangeConfig(c=>c.spatial_minBreathValueReversal = val)}/>
							</RowLR>
							<RowLR mt={5} splitAt={splitAt}>
								<FieldLabel text="Inhale pan direction:" fieldPath={fp("spatial_inhalePanDirection")}/>
								<Select enabled={enabled} displayType="button bar" options={GetEntries(PanDirection, "ui")} value={config.spatial_inhalePanDirection} onChange={val=>ChangeConfig(c=>c.spatial_inhalePanDirection = val)}/>
							</RowLR>
							<RowLR mt={5} splitAt={splitAt}>
								<FieldLabel text="Pan strength:" fieldPath={fp("spatial_panStrength")} info={`
									How much to reduce the volume of the "off" ear when panning. (1 = fully silent, 0 = no reduction)
									Tip: Reduce slightly below 1.0 when using bluetooth earbuds, to keep the idle earbud from going to sleep and emitting an audible wake-up click.
								`.AsMultiline(0)}/>
								<Spinner enabled={enabled} min={0} max={1} step={.05} value={config.spatial_panStrength ?? 1} onChange={val=>ChangeConfig(c=>c.spatial_panStrength = val)}/>
							</RowLR>
							<RowLR mt={5} splitAt={splitAt}>
								<FieldLabel text="Inhale muted:" fieldPath={fp("spatial_inhaleMuted")} info={`
									If enabled, audio is muted during the inhale phase (and audible during exhale).
									If disabled, audio is muted during the exhale phase (and audible during inhale).
								`.AsMultiline(0)}/>
								<CheckBox enabled={enabled} value={config.spatial_inhaleMuted} onChange={val=>ChangeConfig(c=>c.spatial_inhaleMuted = val)}/>
							</RowLR>
						</>}

						<Row mt={15} style={{fontSize: 18, fontWeight: "bold"}}>Drifting mode</Row>

						<Row mt={10} style={{fontWeight: "bold"}}>Desync calculation</Row>
						<RowLR mt={5} splitAt={splitAt}>
							<FieldLabel text="Calc type:" fieldPath={fp("sync_desyncCalcType")} info={`
								Peaks: Can be more comfortable, since the only thing you need to do is make sure you switch inhale->exhale at the siren up->down point.
								Slopes: Can be more reliable, since it doesn't need to explicitly identify what counts as a "peak" or not.
							`.AsMultiline(0)}/>
							<Select enabled={enabled} displayType="button bar" options={GetEntries(DesyncCalcType, "ui")} value={config.sync_desyncCalcType} onChange={val=>ChangeConfig(c=>c.sync_desyncCalcType = val)}/>
						</RowLR>
						<RowLR mt={5} splitAt={splitAt}>
							<FieldLabel text="Breath-peak, min prominence:" fieldPath={fp("sync_minBreathPeakProminence")} info={`
								A peak with prominence of 10% would be one where its "height" is 10% of the "local value span".
								(where "height" = [peak's value - shallower trough's value], and "local value span" = [max - min], of the values in the preceding ${BREATH_PEAKS_CALC_WINDOW_SIZE_DEFAULT / 1000}s window)
							`.AsMultiline(0)}/>
							<SpinnerForPercent enabled={enabled} value={config.sync_minBreathPeakProminence} onChange={val=>ChangeConfig(c=>c.sync_minBreathPeakProminence = val)}/>
							<FieldLabel ml={5} text="Min height:" fieldPath={fp("sync_minBreathPeakHeight")} info={`
								The minimum absolute-height of a breath-peak (initially detected based on relative "prominence") for it to be considered a valid peak.
								Recommended: At least 0.05; this should avoid "peaks" detected when belt is off, while still working when belt is loose (for dreaming periods at least).
							`.AsMultiline(0)}/>
							<Spinner ml={5} enabled={enabled} value={config.sync_minBreathPeakHeight} onChange={val=>ChangeConfig(c=>c.sync_minBreathPeakHeight = val)}/>
						</RowLR>
						<RowLR mt={5} splitAt={splitAt}>
							<FieldLabel text="Response time:" fieldPath={fp("sync_responseTime")} info={`
								When matching breathing-cycle samples with siren-cycle samples, the breathing-data gets "shifted back" by this amount.
								This improves the desync-amount calculation, by giving the user time to shift between inhale<>exhale on hearing the siren shift direction.
							`.AsMultiline(0)}/>
							<Spinner enabled={enabled} min={0} step={0.05} value={config.sync_responseTime} onChange={val=>ChangeConfig(c=>c.sync_responseTime = val)}/>
							<Text ml={5}>s</Text>
						</RowLR>
						<RowLR mt={5} splitAt={splitAt}>
							<FieldLabel text="Calc desync over X seconds:" fieldPath={fp("sync_calcDesyncOverXSeconds_long")} info={`
								The size of the window (in seconds) where breath-samples are read, for calculating the desync amount.
								(There are two windows, long and short; the final siren volume is based on the lower of the two windows' calculated desync-amounts.)
							`.AsMultiline(0)}/>
							<FieldLabel text="Long window:" fieldPath={fp("sync_calcDesyncOverXSeconds_long")}/>
							<Spinner ml={5} enabled={enabled} min={1} step={1} value={config.sync_calcDesyncOverXSeconds_long} onChange={val=>ChangeConfig(c=>c.sync_calcDesyncOverXSeconds_long = val)}/>
							<Text>s</Text>
							<FieldLabel ml={5} text="Short window:" fieldPath={fp("sync_calcDesyncOverXSeconds_short")}/>
							<Spinner ml={5} enabled={enabled} min={1} step={1} value={config.sync_calcDesyncOverXSeconds_short} onChange={val=>ChangeConfig(c=>c.sync_calcDesyncOverXSeconds_short = val)}/>
							<Text>s</Text>
						</RowLR>

						<Row mt={10} style={{fontWeight: "bold"}}>Siren</Row>
						<RowLR mt={5} splitAt={splitAt}>
							<FieldLabel text="Initial cycle time:" fieldPath={fp("sync_cycleTime")}/>
							<Spinner enabled={enabled} min={0} step={0.1} value={config.sync_cycleTime} onChange={val=>ChangeConfig(c=>c.sync_cycleTime = val)}/>
							<Text>s</Text>
							<FieldLabel ml={5} text="Step size:" fieldPath={fp("sync_cycleTime_step")}/>
							<Spinner ml={5} enabled={enabled} min={0} step={0.1} value={config.sync_cycleTime_step} onChange={val=>ChangeConfig(c=>c.sync_cycleTime_step = val)}/>
							<Text>s</Text>
						</RowLR>
						<RowLR mt={5} splitAt={splitAt}>
							<FieldLabel text="Cycle correction interval:" fieldPath={fp("sync_siren_cycleCorrectionInterval")} info={`
								How often to "restart" the siren sound-synthesizer, to ensure the theoretical cycle-interval is aligned with the actual audio.
								(Over the course of a night's sleep, the system and audio-context clock can actually drift apart by multiple seconds!)
							`.AsMultiline(0)}/>
							<TimeSpanInput enabled={enabled} largeUnit="minute" smallUnit="second" value={config.sync_siren_cycleCorrectionInterval} onChange={val=>ChangeConfig(c=>c.sync_siren_cycleCorrectionInterval = val)}/>
						</RowLR>
						<RowLR mt={5} splitAt={splitAt}>
							<FieldLabel text="Siren type:" fieldPath={fp("sync_sirenType")} info={`
								Siren: Sounds like a police siren. Allows for more precise matching of breathing with audio.
								MemoryPalacePrompt: Plays prompts from the memory-palace comp. (which can be helpful for visualization guidance)
							`.AsMultiline(0)}/>
							<Select enabled={enabled} displayType="button bar" options={GetEntries(SirenType, "ui")} value={config.sync_sirenType} onChange={val=>ChangeConfig(c=>c.sync_sirenType = val)}/>
						</RowLR>
						{config.sync_sirenType == SirenType.siren && <>
							<RowLR mt={5} splitAt={splitAt}>
								<FieldLabel text="Min volume:" fieldPath={fp("sync_volume_min")} info="(note: this volume is unaffected by global volume-mod)"/>
								<SpinnerForPercent enabled={enabled} value={config.sync_volume_min} onChange={val=>ChangeConfig(c=>c.sync_volume_min = val)}/>
								<Text ml={5}>Step size:</Text>
								<SpinnerForPercent ml={5} enabled={enabled} min={.01} value={config.sync_volume_min_step} onChange={val=>ChangeConfig(c=>c.sync_volume_min_step = val)}/>
							</RowLR>
							<RowLR mt={5} splitAt={splitAt}>
								<FieldLabel text="Max volume:" fieldPath={fp("sync_volume_max")} info="(note: this volume is unaffected by global volume-mod)"/>
								<SpinnerForPercent enabled={enabled} value={config.sync_volume_max} onChange={val=>ChangeConfig(c=>c.sync_volume_max = val)}/>
							</RowLR>
							<RowLR mt={5} splitAt={splitAt}>
								<FieldLabel text="Desync amount for min volume:" fieldPath={fp("sync_desyncAmountForMinVolume")} info={`
									If the "desync" amount (between siren cycle and breathing cycle) is at this value, the min-volume gets applied. (ie. interpolated between min and max)
									Note: A "100% desync" means that the "slope" (up or down) of every breathing-data sample was opposite the "slope" of the siren's audio-data samples.
								`.AsMultiline(0)}/>
								<SpinnerForPercent enabled={enabled} value={config.sync_desyncAmountForMinVolume} onChange={val=>ChangeConfig(c=>c.sync_desyncAmountForMinVolume = val)}/>
							</RowLR>
							<RowLR mt={5} splitAt={splitAt}>
								<FieldLabel text="Desync amount for max volume:" fieldPath={fp("sync_desyncAmountForMaxVolume")} info={`
									If the "desync" amount (between siren cycle and breathing cycle) is at this value, the max-volume gets applied. (ie. interpolated between min and max)
									Note: A "100% desync" means that the "slope" (up or down) of every breathing-data sample was opposite the "slope" of the siren's audio-data samples.
								`.AsMultiline(0)}/>
								<SpinnerForPercent enabled={enabled} value={config.sync_desyncAmountForMaxVolume} onChange={val=>ChangeConfig(c=>c.sync_desyncAmountForMaxVolume = val)}/>
							</RowLR>
						</>}
						{config.sync_sirenType == SirenType.memoryPalacePrompt && <>
							<RowLR mt={5} splitAt={splitAt}>
								<FieldLabel text="Prompt interval:" fieldPath={fp("sync_memPrompt_interval")}/>
								<Spinner enabled={enabled} value={config.sync_memPrompt_interval} onChange={val=>ChangeConfig(c=>c.sync_memPrompt_interval = val)}/>
								<Text ml={5}>s</Text>
							</RowLR>
							<RowLR mt={5} splitAt={splitAt}>
								<FieldLabel text="Volume sequence:" fieldPath={fp("sync_memPrompt_volumeSequence")} info={`
									Example: "0, 1, 0.66, 0.33" (if cycle-time is 4s and prompt-interval is 1s, result is: 0s=0, 1s=1, 2s=0.66, 3s=0.33, 4s=0, 5s=1, 6s=0.66, etc.)
									(these values chosen, to align peak with police-siren's does [25% through], which is needed [atm!] if using desync-effects)
									Note: These volumes get multiplied by the memory-palace "Voice, volume multiplier" setting.
								`.AsMultiline(0)}/>
								<TextInput enabled={enabled} value={(config.sync_memPrompt_volumeSequence ?? []).join(", ")} onChange={val=>ChangeConfig(c=>c.sync_memPrompt_volumeSequence = val.split(",").map(a=>parseFloat(a)))}/>
							</RowLR>
						</>}

						<Row mt={10} style={{fontWeight: "bold"}}>Lights</Row>
						<RowLR mt={5} splitAt={splitAt}>
							<Text p={flp}>Effect:</Text>
							{/*<EffectPointerInput enabled={enabled} value={config.sync_light_effectPointer} onChange={val=>ChangeConfig(a=>a.sync_light_effectPointer = val)}/>*/}
							<Text style={{fontStyle: "italic"}}>{`(currently uses whatever is set at Alerts -> Effects -> Light effect)`}</Text>
						</RowLR>
						<RowLR mt={5} splitAt={splitAt}>
							<FieldLabel text="Min brightness:" fieldPath={fp("sync_light_brightness_min")}/>
							<SpinnerForPercent enabled={enabled} value={config.sync_light_brightness_min} onChange={val=>ChangeConfig(c=>c.sync_light_brightness_min = val)}/>
						</RowLR>
						<RowLR mt={5} splitAt={splitAt}>
							<FieldLabel text="Max brightness:" fieldPath={fp("sync_light_brightness_max")}/>
							<SpinnerForPercent enabled={enabled} value={config.sync_light_brightness_max} onChange={val=>ChangeConfig(c=>c.sync_light_brightness_max = val)}/>
						</RowLR>
						<RowLR mt={5} splitAt={splitAt}>
							<FieldLabel text="Desync amount for min brightness:" fieldPath={fp("sync_light_desyncAmountForMinBrightness")} info={`
								If the "desync" amount (between siren cycle and breathing cycle) is at this value, the min-brightness gets applied. (ie. interpolated between min and max)
								Note: A "100% desync" means that the "slope" (up or down) of every breathing-data sample was opposite the "slope" of the siren's audio-data samples.
							`.AsMultiline(0)}/>
							<SpinnerForPercent enabled={enabled} value={config.sync_light_desyncAmountForMinBrightness} onChange={val=>ChangeConfig(c=>c.sync_light_desyncAmountForMinBrightness = val)}/>
						</RowLR>
						<RowLR mt={5} splitAt={splitAt}>
							<FieldLabel text="Desync amount for max brightness:" fieldPath={fp("sync_light_desyncAmountForMaxBrightness")} info={`
								If the "desync" amount (between siren cycle and breathing cycle) is at this value, the max-brightness gets applied. (ie. interpolated between min and max)
								Note: A "100% desync" means that the "slope" (up or down) of every breathing-data sample was opposite the "slope" of the siren's audio-data samples.
							`.AsMultiline(0)}/>
							<SpinnerForPercent enabled={enabled} value={config.sync_light_desyncAmountForMaxBrightness} onChange={val=>ChangeConfig(c=>c.sync_light_desyncAmountForMaxBrightness = val)}/>
						</RowLR>

						<Row mt={15}>
							<Text style={{fontSize: 18, fontWeight: "bold"}}>Dreaming mode</Text>
							<InfoButton ml={5} text={`
								While the dynamic siren/lights work well for falling asleep with intention, they can cause premature wakeups when the user is in a dream.
								To avoid this, system can try to detect when a dream has started, and lock the siren/lights to a static state. (eg. one which facilitates WBTB, without ending in-progress dreams)
								Note: Whenever a breath-comp snooze is performed (or "end after" period passes), the mode is switched back to falling-asleep mode.
							`.AsMultiline(0)}/>
						</Row>

						<RowLR mt={10} splitAt={splitAt}>
							<FieldLabel text={`Start when breath-interval (${BREATH_INTERVAL_CALC_WINDOW_SIZE_LONG / minuteInMS}m) drops below:`}
								fieldPath={fp("dream_breathIntervalThreshold")}
								info={`
									Breath-interval threshold below which phase will be switched to dreaming mode. (if <= 0, disabled)
									Note: Not checked if <5m since session start or the last snooze (ie. from micro-snooze comp).
									Note: Also not checked if <${config.sync_blockBreathIntervalEffects_duration ?? "?"}m of last breath-interval calc-block start.
								`}/>
							<Spinner enabled={enabled} value={config.dream_breathIntervalThreshold} onChange={val=>ChangeConfig(c=>c.dream_breathIntervalThreshold = val)}/>
							<Text>s</Text>
						</RowLR>
						<RowLR mt={10} splitAt={splitAt}>
							<FieldLabel text={`End when breath-interval (${BREATH_INTERVAL_CALC_WINDOW_SIZE_LONG / minuteInMS}m) rises above:`}
								fieldPath={fp("dream_endWhenBreathIntervalAbove")} info="(if <= 0, disabled)"/>
							<Spinner enabled={enabled} value={config.dream_endWhenBreathIntervalAbove} onChange={val=>ChangeConfig(c=>c.dream_endWhenBreathIntervalAbove = val)}/>
							<Text>s</Text>
						</RowLR>
						<RowLR mt={10} splitAt={splitAt}>
							<FieldLabel text={`Start when breath-interval ${BREATH_INTERVAL_PERCENTILE_X_THRESHOLD_X.ToPercent(1)}th-${BREATH_INTERVAL_PERCENTILE_X_THRESHOLD_Y.ToPercent(1)}th percentile (${BREATH_INTERVAL_PERCENTILE_X_CALC_WINDOW_SIZE / minuteInMS}m) drops below:`}
								fieldPath={fp("dream_startWhenBreathIntervalLowRangeBelow")} info="(if <= 0, disabled)"/>
							<Spinner enabled={enabled} value={config.dream_startWhenBreathIntervalLowRangeBelow} onChange={val=>ChangeConfig(c=>c.dream_startWhenBreathIntervalLowRangeBelow = val)}/>
							<Text>s</Text>
						</RowLR>
						<RowLR mt={10} splitAt={splitAt}>
							<FieldLabel text={`End when breath-interval ${BREATH_INTERVAL_PERCENTILE_X_THRESHOLD_X.ToPercent(1)}th-${BREATH_INTERVAL_PERCENTILE_X_THRESHOLD_Y.ToPercent(1)}th percentile (${BREATH_INTERVAL_PERCENTILE_X_CALC_WINDOW_SIZE / minuteInMS}m) rises above:`}
								fieldPath={fp("dream_endWhenBreathIntervalLowRangeAbove")} info="(if <= 0, disabled)"/>
							<Spinner enabled={enabled} value={config.dream_endWhenBreathIntervalLowRangeAbove} onChange={val=>ChangeConfig(c=>c.dream_endWhenBreathIntervalLowRangeAbove = val)}/>
							<Text>s</Text>
						</RowLR>
						<RowLR mt={10} splitAt={splitAt}>
							<Text p={flp}>{`End when breath-interval ${BREATH_INTERVAL_PERCENTILE_X_THRESHOLD_X.ToPercent(1)}th-${BREATH_INTERVAL_PERCENTILE_X_THRESHOLD_Y.ToPercent(1)}th percentile (${BREATH_INTERVAL_PERCENTILE_X_CALC_WINDOW_SIZE / minuteInMS}m)...`}</Text>
							<FieldLabel text="...rises above:" fieldPath={fp("dream_ender3_triggerBreathIntervalLowRange")} info="(if <= 0, disabled)"/>
							<Spinner ml={5} enabled={enabled} value={config.dream_ender3_triggerBreathIntervalLowRange} onChange={val=>ChangeConfig(c=>c.dream_ender3_triggerBreathIntervalLowRange = val)}/>
							<FieldLabel ml={5} text="and stays above:" fieldPath={fp("dream_ender3_stayAboveBreathIntervalLowRange")}/>
							<Spinner ml={5} enabled={enabled} value={config.dream_ender3_stayAboveBreathIntervalLowRange} onChange={val=>ChangeConfig(c=>c.dream_ender3_stayAboveBreathIntervalLowRange = val)}/>
							<FieldLabel ml={5} text="for:" fieldPath={fp("dream_ender3_stayAboveDuration")}/>
							<Spinner ml={5} enabled={enabled} value={config.dream_ender3_stayAboveDuration} onChange={val=>ChangeConfig(c=>c.dream_ender3_stayAboveDuration = val)}/>
							<Text ml={5}>m</Text>
						</RowLR>
						<RowLR mt={10} splitAt={splitAt}>
							<FieldLabel text="End after:" fieldPath={fp("dream_endAfter")} info="How long in dreaming mode, before auto-changing back to falling-asleep mode. (set to 0 to disable)"/>
							<TimeSpanInput enabled={enabled} largeUnit="hour" smallUnit="minute" value={config.dream_endAfter} onChange={val=>ChangeConfig(c=>c.dream_endAfter = val)}/>
						</RowLR>
						<RowLR mt={5} splitAt={splitAt}>
							<FieldLabel text="Locked light brightness:" fieldPath={fp("dream_light_brightness")} info="(if <0%, light state is unaffected)"/>
							<SpinnerForPercent enabled={enabled} value={config.dream_light_brightness} onChange={val=>ChangeConfig(c=>c.dream_light_brightness = val)}/>
						</RowLR>

						<Row mt={15} style={{fontSize: 18, fontWeight: "bold"}}>Long inhale</Row>
						<Row mt={5}>
							<Text>Detection configs:</Text>
							<InfoButton ml={5} text={`
								Configures one or more sets of detection criteria for "long inhales".
								A long-inhale is detected if *any* of these configs' criteria are met.
								Detected events can trigger actions (eg. within trigger-sets).
							`.AsMultiline(0)}/>
							<Button ml={5} mdIcon="plus" enabled={enabled} onClick={()=>ChangeConfig(c=>{
								(c.longInhaleDetectionConfigs ??= []).push(new LongInhaleDetectionConfig({
									minDuration: 4, minDuration_phaseDreaming: 3, minAscendHeight: 1, maxRoughness: 0.5,
								}));
							})}/>
						</Row>
						{(config.longInhaleDetectionConfigs ?? []).map((dc, i)=>{
							const ChangeDetectionConfig = (mutator: (c: LongInhaleDetectionConfig)=>void)=>{
								ChangeConfig(c=>{
									const dc2 = c.longInhaleDetectionConfigs[i];
									mutator(dc2);
								});
							};
							return (
								<Column key={i} mt={5} style={{...styles.darkenedBox_configPage}}>
									<Row>
										<Text style={{fontWeight: "bold"}}>Config {i + 1}</Text>
										<Button ml={5} mdIcon="delete" enabled={enabled} onClick={()=>{
											ShowMessageBox({
												title: "Delete detection-config?", cancelButton: true,
												message: `Delete this long-inhale detection-config?`,
												onOK: ()=>{
													ChangeConfig(a=>a.longInhaleDetectionConfigs!.RemoveAt(i));
												},
											});
										}}/>
									</Row>
									<RowLR mt={5} splitAt={splitAt - 10}>
										<Text p={flp}>Min duration:</Text>
										<FieldLabel text="Base:" fieldPath={`breath.longInhaleDetectionConfigs.${i}.minDuration`}/>
										<Spinner ml={5} enabled={enabled} value={dc.minDuration} onChange={val=>ChangeDetectionConfig(c=>c.minDuration = val)}/>
										<Text ml={5}>s</Text>
										<FieldLabel ml={10} text="In dreaming-phase:" fieldPath={`breath.longInhaleDetectionConfigs.${i}.minDuration_phaseDreaming`} info={`
											An adjusted min-duration for signal-detection when in phase "dreaming".
										`.AsMultiline(0)}/>
										<Spinner ml={5} enabled={enabled} value={dc.minDuration_phaseDreaming} onChange={val=>ChangeDetectionConfig(c=>c.minDuration_phaseDreaming = val)}/>
										<Text ml={5}>s</Text>
									</RowLR>
									<RowLR mt={5} splitAt={splitAt - 10}>
										<FieldLabel text="Min ascend height:" fieldPath={`breath.longInhaleDetectionConfigs.${i}.minAscendHeight`} info={`
										The minimum ascend amount/height (in raw respiration-belt value units) of an inhale, to count as a long-inhale.
									`.AsMultiline(0)}/>
										<Spinner enabled={enabled} value={dc.minAscendHeight} onChange={val=>ChangeDetectionConfig(c=>c.minAscendHeight = val)}/>
									</RowLR>
									<RowLR mt={5} splitAt={splitAt - 10}>
										<FieldLabel text="Max roughness:" fieldPath={`breath.longInhaleDetectionConfigs.${i}.maxRoughness`} info={`
										The maximum roughness (variation) in the breath-value of the inhale, to count as a long-inhale.
									`.AsMultiline(0)}/>
										<Spinner enabled={enabled} step={0.05} value={dc.maxRoughness} onChange={val=>ChangeDetectionConfig(c=>c.maxRoughness = val)}/>
									</RowLR>
								</Column>
							);
						})}

						<Row mt={15} style={{fontSize: 18, fontWeight: "bold"}}>DEILD</Row>
						<RowLR mt={5} splitAt={splitAt}>
							<FieldLabel text="DEILD attempt start, trigger:" fieldPath={fp("deild_attemptStart_triggerSet")}/>
							<ControlInput enabled={enabled} value={config.deild_attemptStart_triggerSet} onChange={val=>ChangeConfig(c=>c.deild_attemptStart_triggerSet = val)}/>
						</RowLR>
						<RowLR mt={5} splitAt={splitAt}>
							<FieldLabel text="DEILD attempt start, extra effect:" fieldPath={fp("deild_attemptStart_extraEffect")} info={`
								Effect to play when a DEILD attempt is started. (eg. a TTS message with session-state variables)
							`.AsMultiline(0)}/>
							<EffectPointerInput enabled={enabled} value={config.deild_attemptStart_extraEffect} onChange={val=>ChangeConfig(c=>c.deild_attemptStart_extraEffect = val)}/>
						</RowLR>
						<RowLR mt={5} splitAt={splitAt}>
							<FieldLabel text="DEILD attempt duration:" fieldPath={fp("deild_attemptDuration")} info={`
								Once a DEILD attempt has started, how long to stay in the attempt state (technically stage "solving" atm), before switching back to drifting mode.
							`.AsMultiline(0)}/>
							<Spinner enabled={enabled} value={config.deild_attemptDuration} onChange={val=>ChangeConfig(c=>c.deild_attemptDuration = val)}/>
							<Text ml={5}>m</Text>
						</RowLR>

						<Row mt={15} style={{fontSize: 18, fontWeight: "bold"}}>Alerts</Row>

						<Row mt={5}>
							<CheckBox text="Belt disconnect" enabled={enabled} value={config.alert_beltDC_enabled} onChange={val=>ChangeConfig(c=>c.alert_beltDC_enabled = val)}/>
						</Row>
						{config.alert_beltDC_enabled &&
						<Column ml={10}>
							<RowLR mt={5} splitAt={splitAt - 10}>
								<Text p={flp}>Light effect:</Text>
								<FieldLabel text="Lock lights to:" fieldPath={fp("alert_beltDC_lockLightsToBrightness")} info={`
									Locks lights to brightness X if no breath samples received in last 10s. (<0 = disable light effect)
									Purpose: If belt disconnects during sleep, this alerts you to fix it. (rather than sleeping through whole night)
								`.AsMultiline(0)}/>
								<SpinnerForPercent enabled={enabled} value={config.alert_beltDC_lockLightsToBrightness} onChange={val=>ChangeConfig(c=>c.alert_beltDC_lockLightsToBrightness = val)}/>
							</RowLR>
						</Column>}
						<Row mt={5}>
							<CheckBox text="Belt loose" enabled={enabled} value={config.alert_beltLoose_enabled} onChange={val=>ChangeConfig(c=>c.alert_beltLoose_enabled = val)}/>
						</Row>
						{config.alert_beltLoose_enabled &&
						<Column ml={10}>
							<RowLR mt={5} splitAt={splitAt - 10}>
								<FieldLabel text="Detection start:" fieldPath={fp("alert_beltLoose_detectionStart")} info={`
									How many minutes since session-start or last snooze, before detection begins for the "belt loose" state.
									Note: Belt is detected as "too loose", basically when <6 breath-peaks have been detected in the last 1m.
								`.AsMultiline(0)}/>
								<Spinner enabled={enabled} min={1} value={config.alert_beltLoose_detectionStart} onChange={val=>ChangeConfig(c=>c.alert_beltLoose_detectionStart = val)}/>
								<Text ml={5}>m</Text>
							</RowLR>
							<RowLR mt={5} splitAt={splitAt - 10}>
								<FieldLabel text="Warn interval:" fieldPath={fp("alert_beltLoose_warnInterval")} info={`
									When enabled (ie. >0), voice "Belt too loose" every X seconds that belt is loose.
								`.AsMultiline(0)}/>
								<Spinner enabled={enabled} value={config.alert_beltLoose_warnInterval} onChange={val=>ChangeConfig(c=>c.alert_beltLoose_warnInterval = val)}/>
								<Text ml={5}>s</Text>
							</RowLR>
							<RowLR mt={5} splitAt={splitAt - 10}>
								<Text p={flp}>Light effect:</Text>
								<FieldLabel text="Lock lights after:" fieldPath={fp("alert_beltLoose_lockLightsAfter")} info={`
									How many minutes of belt being in the "too loose" state before the lights are forced on.
								`.AsMultiline(0)}/>
									<Spinner enabled={enabled} value={config.alert_beltLoose_lockLightsAfter} onChange={val=>ChangeConfig(c=>c.alert_beltLoose_lockLightsAfter = val)}/>
									<Text ml={5}>m</Text>
								<FieldLabel ml={10} text="Lock lights to:" fieldPath={fp("alert_beltLoose_lockLightsToBrightness")} info={`
									Locks lights to brightness X if belt has been too loose for the configured duration. (<0 = disable light effect)
								`.AsMultiline(0)}/>
								<SpinnerForPercent enabled={enabled} value={config.alert_beltLoose_lockLightsToBrightness} onChange={val=>ChangeConfig(c=>c.alert_beltLoose_lockLightsToBrightness = val)}/>
							</RowLR>
						</Column>}
					</>}
				</>}
			</Column>
		);
	}
}